/**
 * Supabase Client Configuration
 *
 * This replaces the axios-based API service with Supabase client.
 * All authentication, database queries, and real-time subscriptions go through here.
 *
 * NOTE: The Supabase client instance is imported from lib/supabaseClient.js
 * to ensure we use a single client instance across the application.
 */

import { supabase } from '../lib/supabaseClient';

// ============================================================================
// AUTHENTICATION SERVICE
// ============================================================================

export const authService = {
  /**
   * Login with email and password
   * @param {string} email 
   * @param {string} password 
   * @returns {Promise<{user, session, error}>}
   */
  async login(email, password) {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('Login auth error:', error);
        return { error };
      }

      // Fetch user profile from public.users_pkks table
      const { data: profile, error: profileError } = await supabase
        .from('users_pkks')
        .select('*')
        .eq('id', data.user.id)
        .maybeSingle(); // Use maybeSingle() instead of single() to handle missing profiles

      if (profileError) {
        console.error('Profile fetch error:', profileError);
        return {
          user: data.user,
          session: data.session,
          profile: null,
          error: profileError,
        };
      }

      // If profile doesn't exist, create it from auth metadata
      if (!profile) {
        console.log('Profile not found, creating from auth metadata...');
        const { data: newProfile, error: createError } = await supabase
          .rpc('create_user_ppks_profile', {
            p_id: data.user.id,
            p_email: data.user.email,
            p_full_name: data.user.user_metadata?.full_name || data.user.email?.split('@')[0] || 'User',
            p_role: data.user.user_metadata?.role || 'teacher',
            p_is_anonymous: false
          });

        if (createError) {
          console.error('Profile creation error:', createError);
          return {
            user: data.user,
            session: data.session,
            profile: null,
            error: createError,
          };
        }

        return {
          user: data.user,
          session: data.session,
          profile: newProfile?.[0],
          error: null,
        };
      }

      return {
        user: data.user,
        session: data.session,
        profile,
        error: null,
      };
    } catch (err) {
      console.error('Login exception:', err);
      return { error: err };
    }
  },

  /**
   * Signup new user
   * @param {Object} userData - {email, password, full_name, role, school_id}
   * @returns {Promise<{user, session, error}>}
   */
  async signup({ email, password, full_name, role, school_id, phone }) {
    // Create auth user
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name,
          role,
          school_id,
          phone,
        },
      },
    });

    if (authError) return { error: authError };

    // Create user profile using RPC function for proper enum handling
    const { data: profile, error: profileError } = await supabase
      .rpc('create_user_ppks_profile', {
        p_id: authData.user.id,
        p_email: email,
        p_full_name: full_name,
        p_role: role,
        p_school_id: school_id,
        p_phone: phone,
        p_is_anonymous: false
      });

    return {
      user: authData.user,
      session: authData.session,
      profile: profile?.[0],
      error: profileError,
    };
  },

  /**
   * Logout current user
   */
  async logout() {
    const { error } = await supabase.auth.signOut();
    return { error };
  },

  /**
   * Get current session
   */
  async getSession() {
    const { data, error } = await supabase.auth.getSession();
    return { session: data.session, error };
  },

  /**
   * Get current user profile
   */
  async getCurrentUser() {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    
    if (authError || !user) {
      return { user: null, profile: null, error: authError };
    }

    const { data: profile, error: profileError } = await supabase
      .from('users_pkks')
      .select('*')
      .eq('id', user.id)
      .maybeSingle(); // Use maybeSingle() to handle missing profiles

    return { user, profile, error: profileError };
  },

  /**
   * Create anonymous session for student/industry partner
   * @returns {Promise<{user, session, profile, error}>}
   */
  async createAnonymousSession() {
    try {
      const { data, error } = await supabase.auth.signInAnonymously({
        options: {
          data: {
            anonymous: true,
          },
        },
      });

      if (error) {
        console.error('Anonymous sign-in error:', error);
        return { error };
      }

      if (!data || !data.user) {
        return { error: { message: 'No user data returned' } };
      }

      // Create anonymous user profile using RPC function
      const { data: profile, error: profileError } = await supabase
        .rpc('create_user_ppks_profile', {
          p_id: data.user.id,
          p_email: data.user.email || `anonymous-${data.user.id}@anon.local`,
          p_full_name: 'Anonymous User',
          p_role: 'student', // Default, can be changed
          p_is_anonymous: true
        });

      if (profileError) {
        console.error('Profile creation error:', profileError);
        // Don't fail if profile creation fails - user is still authenticated
      }

      return {
        user: data.user,
        session: data.session,
        profile: profile?.[0] || { id: data.user.id, role: 'student', is_anonymous: true },
        error: null,
      };
    } catch (err) {
      console.error('Anonymous session error:', err);
      return { error: err };
    }
  },

  /**
   * Reset password
   * @param {string} email 
   */
  async forgotPassword(email) {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { data, error };
  },

  /**
   * Update password
   * @param {string} newPassword 
   */
  async updatePassword(newPassword) {
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    return { data, error };
  },
};

// ============================================================================
// ASSESSMENT SERVICE
// ============================================================================

export const assessmentService = {
  /**
   * Create new assessment session
   * @param {Object} sessionData - {user_id, total_questions, is_anonymous}
   * @returns {Promise<{session, error}>}
   */
  async createSession(sessionData) {
    const sessionToken = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const { data, error } = await supabase
      .from('assessment_sessions')
      .insert([
        {
          ...sessionData,
          session_token: sessionToken,
          status: 'not_started',
          current_question_index: 0,
        },
      ])
      .select()
      .single();

    return { session: data, error };
  },

  /**
   * Get assessment session by ID
   * @param {string} sessionId 
   */
  async getSession(sessionId) {
    const { data, error } = await supabase
      .from('assessment_sessions')
      .select('*')
      .eq('id', sessionId)
      .single();

    return { session: data, error };
  },

  /**
   * Update assessment session (for auto-save)
   * @param {string} sessionId 
   * @param {Object} updates 
   */
  async updateSession(sessionId, updates) {
    const { data, error } = await supabase
      .from('assessment_sessions')
      .update(updates)
      .eq('id', sessionId)
      .select()
      .single();

    return { session: data, error };
  },

  /**
   * Auto-save assessment progress
   * @param {string} sessionId 
   * @param {Object} autoSaveData 
   */
  async autoSave(sessionId, autoSaveData) {
    const { data, error } = await supabase
      .from('assessment_sessions')
      .update({
        auto_save_data: autoSaveData,
        updated_at: new Date().toISOString(),
      })
      .eq('id', sessionId)
      .select()
      .single();

    return { session: data, error };
  },

  /**
   * Get questions for specific role
   * @param {string} role - 'teacher', 'student', 'industry_partner'
   */
  async getQuestions(role) {
    const { data, error } = await supabase
      .from('questions')
      .select('*')
      .eq('target_role', role)
      .order('order_index', { ascending: true });

    return { questions: data, error };
  },

  /**
   * Submit response for a question
   * @param {Object} responseData 
   */
  async submitResponse(responseData) {
    const { data, error } = await supabase
      .from('responses')
      .insert([responseData])
      .select()
      .single();

    return { response: data, error };
  },

  /**
   * Submit multiple responses (batch)
   * @param {Array} responses 
   */
  async submitResponses(responses) {
    const { data, error } = await supabase
      .from('responses')
      .insert(responses)
      .select();

    return { responses: data, error };
  },

  /**
   * Get responses for a session
   * @param {string} sessionId 
   */
  async getResponses(sessionId) {
    const { data, error } = await supabase
      .from('responses')
      .select('*, questions(*)')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true });

    return { responses: data, error };
  },

  /**
   * Complete assessment session
   * @param {string} sessionId 
   */
  async completeSession(sessionId) {
    const { data, error } = await supabase
      .from('assessment_sessions')
      .update({
        status: 'completed',
        end_time: new Date().toISOString(),
      })
      .eq('id', sessionId)
      .select()
      .single();

    return { session: data, error };
  },
};

// ============================================================================
// RUBRIC SERVICE (for PKKS Assessment)
// ============================================================================

export const rubricService = {
  /**
   * Get active rubric by applicable role
   * @param {string} applicableTo - 'supervisor', 'teacher', etc.
   * @returns {Promise<{rubric, error}>}
   */
  async getActiveRubric(applicableTo = 'supervisor') {
    // Get all rubrics first to debug
    const { data: allData, error: allError } = await supabase
      .from('pkks_rubric')
      .select('*');

    console.log('All rubrics:', allData, 'Error:', allError);

    // Get the first active rubric
    const { data, error } = await supabase
      .from('pkks_rubric')
      .select('*')
      .eq('is_active', true)
      .limit(1);

    console.log('Active rubrics:', data, 'Error:', error);

    return { rubric: data?.[0] || null, error };
  },

  /**
   * Get rubric by ID
   * @param {string} rubricId
   * @returns {Promise<{rubric, error}>}
   */
  async getRubric(rubricId) {
    const { data, error } = await supabase
      .from('pkks_rubric')
      .select('*')
      .eq('id', rubricId)
      .single();

    return { rubric: data, error };
  },

  /**
   * Get all criteria for a rubric
   * @param {string} rubricId
   * @returns {Promise<{criteria, error}>}
   */
  async getRubricCriteria(rubricId) {
    const { data, error } = await supabase
      .from('pkks_rubric_criteria')
      .select('*')
      .eq('rubric_id', rubricId)
      .order('order_index', { ascending: true });

    return { criteria: data, error };
  },

  /**
   * Get rating levels for a criterion
   * @param {string} criterionId
   * @returns {Promise<{levels, error}>}
   */
  async getRatingLevels(criterionId) {
    const { data, error } = await supabase
      .from('pkks_rubric_rating_levels')
      .select('*')
      .eq('criterion_id', criterionId)
      .order('min_score', { ascending: false });

    return { levels: data, error };
  },

  /**
   * Get all rating levels for a rubric (with criterion data)
   * @param {string} rubricId
   * @returns {Promise<{levels, error}>}
   */
  async getAllRatingLevels(rubricId) {
    const { data, error } = await supabase
      .from('pkks_rubric_rating_levels')
      .select('*, pkks_rubric_criteria(criterion_code, criterion_name)')
      .eq('rubric_id', rubricId)
      .order('min_score', { ascending: false });

    return { levels: data, error };
  },

  /**
   * Get rubric with all criteria and rating levels (full data)
   * @param {string} applicableTo
   * @returns {Promise<{rubric, criteria, error}>}
   */
  async getFullRubric(applicableTo = 'supervisor') {
    const { rubric, error: rubricError } = await this.getActiveRubric(applicableTo);

    if (rubricError || !rubric) {
      return { rubric: null, criteria: null, error: rubricError };
    }

    const { criteria, error: criteriaError } = await this.getRubricCriteria(rubric.id);

    if (criteriaError) {
      return { rubric, criteria: null, error: criteriaError };
    }

    // Get rating levels for each criterion
    const criteriaWithLevels = await Promise.all(
      (criteria || []).map(async (criterion) => {
        const { levels } = await this.getRatingLevels(criterion.id);
        return { ...criterion, rating_levels: levels || [] };
      })
    );

    return { rubric, criteria: criteriaWithLevels, error: null };
  },

  /**
   * Submit a single PKKS assessment response
   * @param {Object} responseData - {session_id, criterion_id, user_id, rubric_id, rating_level, rating_score, evidence_methods_used, notes}
   * @returns {Promise<{response, error}>}
   */
  async submitPkksResponse(responseData) {
    const { data, error } = await supabase
      .from('pkks_assessment_responses')
      .insert([responseData])
      .select()
      .single();

    return { response: data, error };
  },

  /**
   * Submit multiple PKKS assessment responses (batch)
   * @param {Array} responses
   * @returns {Promise<{responses, error}>}
   */
  async submitPkksResponses(responses) {
    const { data, error } = await supabase
      .from('pkks_assessment_responses')
      .insert(responses)
      .select();

    return { responses: data, error };
  },

  /**
   * Get PKKS responses for a session
   * @param {string} sessionId
   * @returns {Promise<{responses, error}>}
   */
  async getPkksResponses(sessionId) {
    const { data, error } = await supabase
      .from('pkks_assessment_responses')
      .select('*, pkks_rubric_criteria(criterion_code, criterion_name, metadata)')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true });

    return { responses: data, error };
  },

  /**
   * Get PKKS assessment summary for a session
   * @param {string} sessionId
   * @returns {Promise<{summary, error}>}
   */
  async getPkksAssessmentSummary(sessionId) {
    const { data, error } = await supabase
      .from('view_pkks_assessment_summary')
      .select('*')
      .eq('session_id', sessionId)
      .single();

    return { summary: data, error };
  },

  /**
   * Update a PKKS response
   * @param {string} responseId
   * @param {Object} updates
   * @returns {Promise<{response, error}>}
   */
  async updatePkksResponse(responseId, updates) {
    const { data, error } = await supabase
      .from('pkks_assessment_responses')
      .update(updates)
      .eq('id', responseId)
      .select()
      .single();

    return { response: data, error };
  },

  /**
   * Delete a PKKS response
   * @param {string} responseId
   * @returns {Promise<{error}>}
   */
  async deletePkksResponse(responseId) {
    const { error } = await supabase
      .from('pkks_assessment_responses')
      .delete()
      .eq('id', responseId);

    return { error };
  },
};

// ============================================================================
// DASHBOARD SERVICE (for Principals)
// ============================================================================

export const dashboardService = {
  /**
   * Get completion rates by role
   */
  async getCompletionRates() {
    const { data, error } = await supabase
      .from('view_completion_rates')
      .select('*');

    return { rates: data, error };
  },

  /**
   * Get recent submissions
   * @param {number} limit 
   */
  async getRecentSubmissions(limit = 50) {
    const { data, error } = await supabase
      .from('view_recent_submissions')
      .select('*')
      .limit(limit);

    return { submissions: data, error };
  },

  /**
   * Get question statistics
   */
  async getQuestionStats() {
    const { data, error } = await supabase
      .from('view_question_stats')
      .select('*');

    return { stats: data, error };
  },

  /**
   * Get dashboard metrics
   * @param {string} schoolId 
   */
  async getMetrics(schoolId = null) {
    let query = supabase
      .from('dashboard_metrics')
      .select('*')
      .order('computed_at', { ascending: false });

    if (schoolId) {
      query = query.eq('school_id', schoolId);
    }

    const { data, error } = await query;
    return { metrics: data, error };
  },

  /**
   * Get all sessions with filters
   * @param {Object} filters - {role, status, school_id, date_from, date_to}
   */
  async getSessions(filters = {}) {
    let query = supabase
      .from('assessment_sessions')
      .select('*, users(full_name, role, school_id)')
      .order('start_time', { ascending: false });

    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    if (filters.date_from) {
      query = query.gte('start_time', filters.date_from);
    }

    if (filters.date_to) {
      query = query.lte('start_time', filters.date_to);
    }

    const { data, error } = await query;
    return { sessions: data, error };
  },
};

// ============================================================================
// REPORT SERVICE
// ============================================================================

export const reportService = {
  /**
   * Generate report
   * @param {Object} reportData - {title, report_type, filters, report_data}
   */
  async generateReport(reportData) {
    const { data: { user } } = await supabase.auth.getUser();

    const { data, error } = await supabase
      .from('reports')
      .insert([
        {
          ...reportData,
          generated_by: user?.id,
        },
      ])
      .select()
      .single();

    return { report: data, error };
  },

  /**
   * Get all reports
   * @param {string} reportType - Optional filter by type
   */
  async getReports(reportType = null) {
    let query = supabase
      .from('reports')
      .select('*, users(full_name)')
      .order('generated_at', { ascending: false });

    if (reportType) {
      query = query.eq('report_type', reportType);
    }

    const { data, error } = await query;
    return { reports: data, error };
  },

  /**
   * Download report file
   * @param {string} reportId 
   */
  async downloadReport(reportId) {
    const { data, error } = await supabase
      .from('reports')
      .select('file_url')
      .eq('id', reportId)
      .single();

    return { fileUrl: data?.file_url, error };
  },
};

// ============================================================================
// NOTIFICATION SERVICE
// ============================================================================

export const notificationService = {
  /**
   * Get user notifications
   * @param {boolean} unreadOnly 
   */
  async getNotifications(unreadOnly = false) {
    const { data: { user } } = await supabase.auth.getUser();

    let query = supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user?.id)
      .order('created_at', { ascending: false });

    if (unreadOnly) {
      query = query.eq('is_read', false);
    }

    const { data, error } = await query;
    return { notifications: data, error };
  },

  /**
   * Mark notification as read
   * @param {string} notificationId 
   */
  async markAsRead(notificationId) {
    const { data, error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('id', notificationId)
      .select()
      .single();

    return { notification: data, error };
  },

  /**
   * Mark all notifications as read
   */
  async markAllAsRead() {
    const { data: { user } } = await supabase.auth.getUser();

    const { data, error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('user_id', user?.id)
      .eq('is_read', false);

    return { count: data?.length, error };
  },
};

// ============================================================================
// REAL-TIME SUBSCRIPTIONS
// ============================================================================

/**
 * Subscribe to real-time changes on a table
 * @param {string} table - Table name
 * @param {Function} callback - Called when data changes
 * @param {Object} filters - Optional filters (e.g., {status: 'completed'})
 * @returns {Object} subscription - Call .unsubscribe() to stop listening
 */
export function subscribeToTable(table, callback, filters = {}) {
  let subscription = supabase
    .channel(`public:${table}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: table,
        filter: Object.entries(filters)
          .map(([key, value]) => `${key}=eq.${value}`)
          .join(','),
      },
      (payload) => {
        callback(payload);
      }
    )
    .subscribe();

  return subscription;
}

/**
 * Subscribe to new submissions (for Principal dashboard)
 */
export function subscribeToNewSubmissions(callback) {
  return subscribeToTable('assessment_sessions', (payload) => {
    if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
      callback(payload.new);
    }
  });
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Check if user is authenticated
 */
export async function isAuthenticated() {
  const { data: { session } } = await supabase.auth.getSession();
  return !!session;
}

/**
 * Get current user role
 */
export async function getCurrentUserRole() {
  const { profile } = await authService.getCurrentUser();
  return profile?.role;
}

/**
 * Check if user has specific role
 * @param {string} role 
 */
export async function hasRole(role) {
  const currentRole = await getCurrentUserRole();
  return currentRole === role;
}

/**
 * Check if user is principal
 */
export async function isPrincipal() {
  return await hasRole('principal');
}

// ============================================================================
// PARENT ASSESSMENT SERVICE
// ============================================================================

export const parentAssessmentService = {
  /**
   * Get all parent assessment questions grouped by component
   * @returns {Promise<{questions, rawQuestions, error}>}
   */
  async getQuestions() {
    const { data, error } = await supabase
      .from('parent_assessment_questions')
      .select('*')
      .eq('is_active', true)
      .order('component_order', { ascending: true })
      .order('statement_order', { ascending: true });

    if (error) return { questions: null, rawQuestions: null, error };

    // Group by component
    const grouped = data.reduce((acc, q) => {
      if (!acc[q.component_name]) {
        acc[q.component_name] = [];
      }
      acc[q.component_name].push(q);
      return acc;
    }, {});

    return { questions: grouped, rawQuestions: data, error: null };
  },

  /**
   * Create session for authenticated parent
   * @param {string} userId
   * @param {string} targetSchoolId
   * @returns {Promise<{session, error}>}
   */
  async createSession(userId, targetSchoolId) {
    const sessionToken = `parent_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const { data, error } = await supabase
      .from('assessment_sessions')
      .insert([{
        user_id: userId,
        session_token: sessionToken,
        status: 'not_started',
        total_questions: 17,
        is_anonymous: false,
        target_school_id: targetSchoolId,
        auto_save_data: { role: 'parent' }
      }])
      .select()
      .single();

    return { session: data, error };
  },

  /**
   * Submit parent responses
   * @param {string} sessionId
   * @param {Array} responses
   * @param {string} userId
   * @returns {Promise<{responses, error}>}
   */
  async submitResponses(sessionId, responses, userId) {
    const responsesArray = responses.map(r => ({
      session_id: sessionId,
      question_id: r.question_id,
      user_id: userId,
      rating_value: r.rating_value
    }));

    const { data, error } = await supabase
      .from('parent_assessment_responses')
      .insert(responsesArray)
      .select();

    return { responses: data, error };
  },

  /**
   * Get responses by user
   * @param {string} userId
   * @returns {Promise<{responses, error}>}
   */
  async getResponsesByUser(userId) {
    const { data, error } = await supabase
      .from('parent_assessment_responses')
      .select(`
        id,
        rating_value,
        created_at,
        parent_assessment_questions(component_name, statement_text)
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    return { responses: data, error };
  },

  /**
   * Get parent assessment summary by school
   * @param {string} schoolId
   * @returns {Promise<{summary, error}>}
   */
  async getSummaryBySchool(schoolId) {
    const { data, error } = await supabase
      .from('view_parent_assessment_summary')
      .select('*')
      .eq('school_id', schoolId);

    return { summary: data, error };
  },

  /**
   * Complete assessment session
   * @param {string} sessionId
   * @returns {Promise<{session, error}>}
   */
  async completeSession(sessionId) {
    const { data, error } = await supabase
      .from('assessment_sessions')
      .update({
        status: 'completed',
        end_time: new Date().toISOString(),
      })
      .eq('id', sessionId)
      .select()
      .single();

    return { session: data, error };
  }
};

// ============================================================================
// PRESTASI SERVICE (for weekly achievements)
// ============================================================================

export const prestasiService = {
  /**
   * Upload a single photo to the 'prestasi-foto' public S3 bucket.
   * Returns only the public URL — never stores raw blob data in the DB.
   *
   * @param {File} file - Image file (JPEG, PNG, WebP; max 5 MB)
   * @param {string} prestasiId - UUID of the prestasi_mingguan record (used as folder name)
   * @param {number} fotoIndex - Sequential index used in the file path
   * @returns {Promise<{publicUrl, error}>}
   */
  async uploadFoto(file, prestasiId, fotoIndex) {
    const BUCKET = 'prestasi-foto';
    const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    const MAX_SIZE = 5 * 1024 * 1024; // 5 MB

    if (!ALLOWED_TYPES.includes(file.type)) {
      return { publicUrl: null, error: new Error('Format file tidak didukung. Gunakan JPEG, PNG, atau WebP.') };
    }

    if (file.size > MAX_SIZE) {
      return { publicUrl: null, error: new Error('Ukuran file terlalu besar. Maksimum 5 MB.') };
    }

    const ext = file.name.split('.').pop();
    const filePath = `${prestasiId}/foto_${fotoIndex}.${ext}`;

    // Upload file to public S3 bucket
    const { error: uploadError } = await supabase.storage
      .from(BUCKET)
      .upload(filePath, file, { cacheControl: '3600', upsert: true });

    if (uploadError) return { publicUrl: null, error: uploadError };

    // Get and return only the public URL (never the blob)
    const { data: { publicUrl } } = supabase.storage
      .from(BUCKET)
      .getPublicUrl(filePath);

    return { publicUrl, error: null };
  },

  /**
   * Upload any number of photos for a prestasi entry.
   * Returns a photo_urls array of public URLs (skips failed uploads).
   *
   * @param {File[]} files - Array of image files (no hard limit on count)
   * @param {string} prestasiId - UUID of the prestasi_mingguan record
   * @returns {Promise<{photo_urls: string[], errors: string[]}>}
   */
  async uploadFotos(files, prestasiId) {
    const photo_urls = [];
    const errors = [];

    await Promise.all(
      files.map(async (file, idx) => {
        if (!file) return;
        const { publicUrl, error } = await this.uploadFoto(file, prestasiId, idx + 1);
        if (error) {
          errors.push(`Foto ${idx + 1}: ${error.message}`);
        } else {
          photo_urls[idx] = publicUrl; // preserve order
        }
      })
    );

    return { photo_urls: photo_urls.filter(Boolean), errors };
  },

  /**
   * Create a prestasi_mingguan record.
   * Accepts photo_urls as an array of public bucket URLs (not File objects).
   *
   * @param {Object} data - { sekolah_id?, nama_sekolah, judul, deskripsi?, photo_urls?, tanggal_prestasi? }
   * @returns {Promise<{prestasi, error}>}
   */
  async createPrestasi(data) {
    const { data: prestasi, error } = await supabase
      .from('prestasi_mingguan')
      .insert([{ photo_urls: [], ...data, is_published: true }])
      .select()
      .single();

    return { prestasi, error };
  },

  /**
   * Fetch published prestasi entries for the current week.
   *
   * @param {string} weekStartIso - ISO date string for week start (YYYY-MM-DD)
   * @param {string} weekEndIso - ISO date string for week end (YYYY-MM-DD)
   * @param {number} limit
   * @returns {Promise<{data, error}>}
   */
  async getWeeklyPrestasi(weekStartIso, weekEndIso, limit = 6) {
    return supabase
      .from('prestasi_mingguan')
      .select('*')
      .eq('is_published', true)
      .gte('tanggal_prestasi', weekStartIso)
      .lte('tanggal_prestasi', weekEndIso)
      .order('tanggal_prestasi', { ascending: false })
      .limit(limit);
  }
};

// PRINCIPAL SCORE SERVICE
// ============================================================================

export const principalScoreService = {
  /**
   * Get all principals with their schools
   * @returns {Promise<{principals, error}>}
   */
  async getPrincipals() {
    try {
      // Get principals with their school info
      const { data: principals, error: principalError } = await supabase
        .from('users_pkks')
        .select(`
          id,
          full_name,
          school_id,
          email
        `)
        .eq('role', 'principal')
        .eq('is_active', true)
        .order('full_name');

      if (principalError) return { principals: null, error: principalError };

      // Get school details
      const schoolIds = principals?.map(p => p.school_id).filter(Boolean) || [];

      if (schoolIds.length === 0) {
        return { principals: principals?.map(p => ({
          ...p,
          school_name: 'Unknown School',
          npsn: null
        })), error: null };
      }

      // Try to match by npsn OR by id (school_id could be either)
      const { data: schools, error: schoolError } = await supabase
        .from('sekolah')
        .select('id, npsn, nama')
        .or(`npsn.in.(${schoolIds.join(',')}),id.in.(${schoolIds.join(',')})`);

      if (schoolError) {
        console.error('Error fetching schools:', schoolError);
      }

      // Merge principal and school data
      const principalsWithSchools = principals?.map(p => {
        // Try matching by npsn first, then by id
        const school = schools?.find(s => s.npsn === p.school_id || s.id === p.school_id);
        return {
          ...p,
          school_name: school?.nama || 'Unknown School',
          npsn: school?.npsn || p.school_id
        };
      });

      return { principals: principalsWithSchools, error: null };
    } catch (err) {
      console.error('Error in getPrincipals:', err);
      return { principals: null, error: err };
    }
  },

  /**
   * Get assessment scores for a principal by school_id
   * @param {string} schoolId
   * @returns {Promise<{scores, error}>}
   */
  async getPrincipalScores(schoolId) {
    try {
      const scores = {
        teacher: null,
        parent: null,
        supervisor: null,
        student: null,
        industry_partner: null
      };

      // Get all completed sessions with target_school_id
      const { data: allSessions, error: sessionsError } = await supabase
        .from('assessment_sessions')
        .select(`
          id,
          user_id,
          auto_save_data,
          target_school_id,
          status
        `)
        .eq('status', 'completed');

      if (sessionsError) {
        console.error('Error fetching sessions:', sessionsError);
        return { scores, error: null };
      }

      // Get user details for all sessions
      const userIds = [...new Set(allSessions?.map(s => s.user_id).filter(Boolean))];

      let usersMap = {};
      if (userIds.length > 0) {
        const { data: users, error: usersError } = await supabase
          .from('users_pkks')
          .select('id, school_id, role')
          .in('id', userIds);

        if (!usersError && users) {
          users.forEach(u => {
            usersMap[u.id] = u;
          });
        }
      }

      // Helper to get session IDs by role and school
      // Uses target_school_id if set, falls back to user's school_id for legacy sessions
      const getSessionIdsByRole = (role) => {
        return (allSessions || [])
          .filter(s => {
            const user = usersMap[s.user_id];
            const userRole = user?.role || s.auto_save_data?.role;

            // Match role
            if (userRole !== role) return false;

            // Use target_school_id if set (new sessions)
            if (s.target_school_id) {
              return s.target_school_id === schoolId ||
                     String(s.target_school_id) === String(schoolId);
            }

            // Fallback to user's school_id (legacy sessions)
            const userSchoolId = user?.school_id;
            return userSchoolId === schoolId ||
                   String(userSchoolId) === String(schoolId);
          })
          .map(s => s.id);
      };

      // Helper to calculate scores from responses
      const calculateScoresFromResponses = (responses, targetRole) => {
        if (!responses || responses.length === 0) return null;

        const totalScore = responses.reduce((sum, r) => {
          const value = parseFloat(r.response_value?.value || r.response_value || 0);
          return sum + (isNaN(value) ? 0 : value);
        }, 0);

        const avgScore = totalScore / responses.length;

        const categories = {};
        responses.forEach(r => {
          const cat = r.questions?.category || 'Uncategorized';
          if (!categories[cat]) {
            categories[cat] = { total: 0, count: 0 };
          }
          const value = parseFloat(r.response_value?.value || r.response_value || 0);
          if (!isNaN(value)) {
            categories[cat].total += value;
            categories[cat].count += 1;
          }
        });

        const components = Object.entries(categories).map(([category, data]) => ({
          category,
          avg_score: data.count > 0 ? data.total / data.count : 0,
          count: data.count
        }));

        return {
          avg_score: avgScore || 0,
          total_score: totalScore,
          total_responses: responses.length,
          unique_evaluators: new Set(responses.map(r => r.session_id)).size,
          components
        };
      };

      // 1. Get Teacher Assessment Scores
      const teacherSessionIds = getSessionIdsByRole('teacher');
      if (teacherSessionIds.length > 0) {
        const { data: teacherResponses } = await supabase
          .from('responses')
          .select(`
            id,
            response_value,
            session_id,
            questions(category, target_role)
          `)
          .in('session_id', teacherSessionIds);

        if (teacherResponses && teacherResponses.length > 0) {
          scores.teacher = calculateScoresFromResponses(teacherResponses, 'teacher');
        }
      }

      // 2. Get Parent Assessment Scores
      const { data: parentSummary, error: parentError } = await supabase
        .from('view_parent_assessment_summary')
        .select('*')
        .eq('school_id', schoolId);

      if (!parentError && parentSummary && parentSummary.length > 0) {
        const totalResponses = parentSummary.reduce((sum, s) => sum + (s.total_responses || 0), 0);
        const totalScore = parentSummary.reduce((sum, s) => sum + (s.total_score || 0), 0);
        const avgScore = totalResponses > 0 ? totalScore / totalResponses : 0;

        scores.parent = {
          avg_score: avgScore,
          total_score: totalScore,
          total_responses: totalResponses,
          components: parentSummary.map(s => ({
            component_name: s.component_name,
            avg_score: parseFloat(s.average_score) || 0,
            total_responses: s.total_responses
          }))
        };
      }

      // 3. Get Supervisor Assessment Scores (PKKS Rubric)
      const supervisorSessionIds = getSessionIdsByRole('supervisor');
      if (supervisorSessionIds.length > 0) {
        const { data: pkksResponses, error: pkksError } = await supabase
          .from('pkks_assessment_responses')
          .select(`
            id,
            rating_score,
            rating_level,
            criterion_id,
            pkks_rubric_criteria(criterion_name, criterion_code)
          `)
          .in('session_id', supervisorSessionIds);

        if (!pkksError && pkksResponses && pkksResponses.length > 0) {
          const totalScore = pkksResponses.reduce((sum, r) => sum + (r.rating_score || 0), 0);
          const avgScore = totalScore / pkksResponses.length;

          const criteria = {};
          pkksResponses.forEach(r => {
            const criterionId = r.criterion_id;
            if (!criteria[criterionId]) {
              criteria[criterionId] = {
                criterion_name: r.pkks_rubric_criteria?.criterion_name || 'Unknown',
                criterion_code: r.pkks_rubric_criteria?.criterion_code || '-',
                total: 0,
                count: 0,
                levels: {}
              };
            }
            criteria[criterionId].total += r.rating_score || 0;
            criteria[criterionId].count += 1;
            const level = r.rating_level || 'unknown';
            criteria[criterionId].levels[level] = (criteria[criterionId].levels[level] || 0) + 1;
          });

          const components = Object.values(criteria).map(c => {
            const mostCommonLevel = Object.entries(c.levels)
              .sort((a, b) => b[1] - a[1])[0]?.[0] || 'satisfactory';

            return {
              criterion_name: c.criterion_name,
              criterion_code: c.criterion_code,
              avg_score: c.count > 0 ? c.total / c.count : 0,
              rating_level: mostCommonLevel
            };
          });

          scores.supervisor = {
            avg_score: avgScore,
            total_score: totalScore,
            total_responses: pkksResponses.length,
            unique_evaluators: new Set(supervisorSessionIds).size,
            components
          };
        }
      }

      // 4. Get Student Survey Scores
      const studentSessionIds = getSessionIdsByRole('student');
      if (studentSessionIds.length > 0) {
        const { data: studentResponses } = await supabase
          .from('responses')
          .select(`
            id,
            response_value,
            session_id,
            questions(category, target_role)
          `)
          .in('session_id', studentSessionIds);

        if (studentResponses && studentResponses.length > 0) {
          scores.student = calculateScoresFromResponses(studentResponses, 'student');
        }
      }

      // 5. Get Industry Partner Assessment Scores
      const industrySessionIds = getSessionIdsByRole('industry_partner');
      if (industrySessionIds.length > 0) {
        const { data: industryResponses } = await supabase
          .from('responses')
          .select(`
            id,
            response_value,
            session_id,
            questions(category, target_role)
          `)
          .in('session_id', industrySessionIds);

        if (industryResponses && industryResponses.length > 0) {
          scores.industry_partner = calculateScoresFromResponses(industryResponses, 'industry_partner');
        }
      }

      return { scores, error: null };
    } catch (err) {
      console.error('Error in getPrincipalScores:', err);
      return { scores: null, error: err };
    }
  },

  /**
   * Get summary scores for all principals (for admin/supervisor view)
   * @returns {Promise<{summaries, error}>}
   */
  async getAllPrincipalSummaries() {
    try {
      const { principals, error: principalError } = await this.getPrincipals();

      if (principalError) return { summaries: null, error: principalError };

      const summaries = await Promise.all(
        (principals || []).map(async (principal) => {
          const { scores } = await this.getPrincipalScores(principal.school_id);
          return {
            ...principal,
            scores
          };
        })
      );

      return { summaries, error: null };
    } catch (err) {
      console.error('Error in getAllPrincipalSummaries:', err);
      return { summaries: null, error: err };
    }
  }
};

// ============================================================================
// SCHOOL SERVICE
// ============================================================================

export const schoolService = {
  /**
   * Get all schools for selection dropdown
   * @returns {Promise<{schools, error}>}
   */
  async getSchools() {
    const { data, error } = await supabase
      .from('sekolah')
      .select('id, npsn, nama, kota, kecamatan')
      .eq('status', 'aktif')
      .order('nama', { ascending: true });

    return { schools: data, error };
  },

  /**
   * Get school by ID or NPSN
   * @param {string} schoolId - UUID or NPSN
   * @returns {Promise<{school, error}>}
   */
  async getSchool(schoolId) {
    // Check if schoolId looks like a UUID (contains dashes and is 36 chars)
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(schoolId);

    let query = supabase
      .from('sekolah')
      .select('*');

    if (isUUID) {
      query = query.eq('id', schoolId);
    } else {
      // Assume it's an NPSN
      query = query.eq('npsn', schoolId);
    }

    const { data, error } = await query.maybeSingle();

    return { school: data, error };
  },

  /**
   * Get principals by school
   * @param {string} schoolId
   * @returns {Promise<{principals, error}>}
   */
  async getPrincipalsBySchool(schoolId) {
    const { data, error } = await supabase
      .from('users_pkks')
      .select('id, full_name, email')
      .eq('role', 'principal')
      .eq('school_id', schoolId)
      .eq('is_active', true);

    return { principals: data, error };
  }
};

// Re-export supabase client for convenience
export { supabase };
